<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldObservation } from "@/types/field-observations.types";
import {
  getObservationTitle,
  getObservationTypeConfig,
} from "@/components/field-observations/observation-type";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Une observation listée sous sa partie, dans l'onglet Parties : sa lettre
 * (celle de son cadre sur le plan), son type, son titre. La toucher la montre
 * sur le plan — sauf si elle n'y a aucun élément (« hors plan ») : elle
 * reste listée, mais ne se classe que depuis sa fiche.
 */

const props = defineProps<{
  observation: FieldObservation;
  /** Lettre de l'observation (ordre de création). */
  letter: string;
  /** Elle a des éléments sur ce plan. */
  onPlan: boolean;
  /** Mise en avant sur le plan. */
  current: boolean;
}>();

defineEmits<{
  (e: "select"): void;
}>();

const { t } = useTranslation();

const typeConfig = computed(() =>
  getObservationTypeConfig(props.observation.type),
);
const title = computed(() => getObservationTitle(props.observation, t));
</script>

<template>
  <button
    type="button"
    class="observation-row d-flex align-items-center gap-2"
    :class="{ current, 'off-plan': !onPlan }"
    :disabled="!onPlan"
    @click="$emit('select')"
  >
    <span class="row-letter flex-shrink-0">{{ letter }}</span>
    <PhosphorIcon
      :name="typeConfig.icon"
      :size="13"
      weight="bold"
      class="type-icon flex-shrink-0"
    />
    <span class="text-truncate flex-grow-1">{{ title }}</span>
    <span v-if="!onPlan" class="off-plan-tag flex-shrink-0">
      {{ t("canvas:observations.parts.offPlan") }}
    </span>
  </button>
</template>

<style lang="scss" scoped>
.observation-row {
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  font-size: 0.82rem;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;

  &.current {
    background: color-mix(in srgb, var(--bs-primary) 18%, transparent);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--bs-primary) 18%, transparent);
    }
  }

  &.off-plan {
    cursor: default;
    opacity: 0.6;
  }
}

// Icône du type, dans le bleu des observations (éclairci pour le fond sombre).
.type-icon {
  color: color-mix(in srgb, var(--bs-primary) 65%, white);
}

.row-letter {
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  background-color: var(--bs-primary);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.off-plan-tag {
  font-size: 0.66rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  white-space: nowrap;
}
</style>
